import { useMutation, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useLocation, useParams } from 'react-router-dom'

import useAuth from 'data/gradoo/hooks/useAuth'
import { SWITCH_COVER_TEMPLATE } from 'data/layoutcreator/mutations/cover'
import {
  GET_COVER_TEMPLATES,
  GET_COVER_TEMPLATE_CATEGORIES
} from 'data/layoutcreator/queries/cover'
import { GET_PROJECT_COVER_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import SecondLevelTitle from 'shared/components/Tools/components/PanelSecondLevelTitle'
import { CoverSides } from 'shared/types/global'
import Flex from 'shared/ui/Flex'
import Select from 'shared/ui/SelectOld'
import CustomScrollbars from 'shared/ui/SimpleBar'

import TemplateItem from './components/TemplateItem'
import TemplateItemSkeleton from './components/TemplateItem/Skeleton'
import WarningModal from './components/WarningModal'

type SwitchTemplatePanelProps = {
  onBackButtonClick: () => void
}

const SwitchTemplatePanel: React.FC<SwitchTemplatePanelProps> = ({
  onBackButtonClick
}) => {
  const { authGroupId: groupId } = useAuth()
  const { projectId } = useParams()
  const { pathname } = useLocation()
  const isInnerCover = pathname.split('/').pop() === 'inside'

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)

  const { formatMessage } = useIntl()
  const [selectedCategoryIndex, setSelectedCategoryIndex] =
    useState(0)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')

  const { data: categoriesData } = useQuery(
    GET_COVER_TEMPLATE_CATEGORIES
  )
  const { data: templatesData, loading: templatesLoading } = useQuery(
    GET_COVER_TEMPLATES
  )
  const [switchTemplate] = useMutation(SWITCH_COVER_TEMPLATE)

  const { refetch: refetchCoverDocuments } = useQuery(
    GET_PROJECT_COVER_DOCUMENTS
  )

  const categories = useMemo(
    () => [
      formatMessage({
        id: 'Panel.switchCoverTemplate.categories.all'
      }),
      ...(categoriesData?.getCoverTemplateCategories || [])
    ],
    [formatMessage, categoriesData?.getCoverTemplateCategories]
  )
  const templates = useMemo(() => {
    const allTemplates = templatesData?.getCoverTemplates || []
    const selectedCategory = categories[selectedCategoryIndex]

    if (selectedCategoryIndex === 0) {
      return allTemplates
    }

    return allTemplates.filter(
      template => template.category === selectedCategory
    )
  }, [templatesData, selectedCategoryIndex, categories])

  const handleSwitchTemplate = async (templateId: string) => {
    setIsWarningModalOpen(false)

    const loadingToast = toast.loading(
      formatMessage({ id: 'Panel.switchCoverTemplate.loading' })
    )

    try {
      await switchTemplate({
        variables: {
          groupId: groupId!,
          projectId: projectId!,
          templatePath: templates.find(
            template => template.id === templateId
          )?.path!,
          side: isInnerCover ? CoverSides.inside : CoverSides.outside
        }
      })

      toast.success(
        formatMessage({ id: 'Panel.switchCoverTemplate.success' }),
        {
          id: loadingToast
        }
      )
    } catch (error) {
      toast.error(
        formatMessage({ id: 'Panel.switchCoverTemplate.error' }),
        {
          id: loadingToast
        }
      )
    }

    await refetchCoverDocuments({
      groupId: groupId!,
      projectId: projectId!
    })
  }

  return (
    <CustomScrollbars
      fixedHeaderMarginBottom={124}
      fixedHeader={
        <SecondLevelTitle
          titleId="Panel.switchCoverTemplate.title"
          onClick={onBackButtonClick}
        />
      }
    >
      <Flex direction="column">
        <Select
          label={formatMessage({
            id: `Panel.switchCoverTemplate.categories.label`
          })}
          options={categories}
          activeIndex={selectedCategoryIndex}
          onChange={(_, index) => setSelectedCategoryIndex(index)}
        />

        <Flex direction="column" rowGap={16} marginTop={32}>
          {templatesLoading ? (
            // Show skeleton item while loading
            <TemplateItemSkeleton />
          ) : (
            templates.map(template => (
              <TemplateItem
                key={template.id}
                name={template.name}
                image={template.previewUrl}
                isSelected={template.id === selectedTemplateId}
                onSelectTemplate={() => {
                  setSelectedTemplateId(template.id)
                  setIsWarningModalOpen(true)
                }}
              />
            ))
          )}
        </Flex>
      </Flex>

      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        onSwitchTemplate={() =>
          handleSwitchTemplate(selectedTemplateId)
        }
      />
    </CustomScrollbars>
  )
}

export default SwitchTemplatePanel
